import Vue from 'vue';
import Vuex from 'vuex';
import fetcher from '@/fetcher';
import config from '@/config';
import isEmpty from 'lodash/isEmpty';

Vue.use(Vuex);

const apiHost = config.apiHost;

const fakeShop = {
  id: 'random-shop-1',
  name: 'Random Shop Name',
  unit: '#01-01',
  address: 'ABC 123, #01-01',
  rating: 4,
  pricing: 2,
  category_id: 1,
};
const fakeShopList = [fakeShop, fakeShop, fakeShop, fakeShop, fakeShop];

interface ILocation {
  lat: number;
  lon: number;
}

type OpenHours = Array<{
  day: number;
  open: {
    starttime: number;
    endtime: number;
    close?: boolean;
  };
}>;

export interface IMall {
  id: string;
  name: string;
  address: string;
  description?: string;
  rating?: number;
  active: boolean;
  location: ILocation;
  openHours?: OpenHours;
  ownerId?: string;
}

export interface IBusinessSummary {
  id: string;
  name: string;
  description?: string;
  unit?: string;
  rating?: number;
  pricing?: number;
  categoryId?: number;
}

export interface IBusinessDetails extends IBusinessSummary {
  mallId?: string;
  active: boolean;
  phone?: string;
  location: ILocation;
  openHours?: OpenHours;
  ownerId?: string;
}

export interface IBusinessCategoryMap {
  [id: number]: { name: string };
}

export interface IStore {
  directoryList: any[];
  mallDetailsMap: { [id: string]: IMall };
  mallDir: { [id: string]: IBusinessSummary[] };
  nearbyMalls: IMall[];
  bizDetailsMap: { [id: string]: IBusinessDetails };
  bizCategories: IBusinessCategoryMap;
}

export enum actions {
  fetchNearbyMalls = 'fetchNearbyMalls',
  fetchMallDetails = 'fetchMallDetails',
  fetchMallDir = 'fetchMallDir',
  fetchBizDetails = 'fetchBizDetails',
  fetchBizCategories = 'fetchBizCategories',
}

export default new Vuex.Store<IStore>({
  state: {
    directoryList: [],
    mallDetailsMap: {},
    mallDir: {},
    nearbyMalls: [],
    bizDetailsMap: {},
    bizCategories: {},
  },
  mutations: {
    setMallDetailsMap(state, updatedMallDetailsMap) {
      state.mallDetailsMap = updatedMallDetailsMap;
    },
    setMallDir(state, updatedMallDir) {
      state.mallDir = updatedMallDir;
    },
    setNearbyMalls(state, updatedNearbyMalls) {
      state.nearbyMalls = updatedNearbyMalls;
    },
    setBizDetailsMap(state, updatedBizDetailsMap) {
      state.bizDetailsMap = updatedBizDetailsMap;
    },
    setBizCategories(state, updatedBizCategories) {
      state.bizCategories = updatedBizCategories;
    },
  },
  actions: {
    async [actions.fetchNearbyMalls]({ state, commit }) {
      if (state.nearbyMalls.length) {
        return;
      }

      const data = await fetcher(`${apiHost}/malls`)
        .get()
        .json<IMall[]>();

      const newNearbyMalls = [...data];

      commit('setNearbyMalls', newNearbyMalls);

      // Also update mall details while we're at it
      const newMallDetailsMap = { ...state.mallDetailsMap };

      for (const mall of data) {
        newMallDetailsMap[mall.id] = mall;
      }

      commit('setMallDetailsMap', newMallDetailsMap);
    },
    async [actions.fetchMallDetails]({ state, commit }, mallId) {
      if (state.mallDetailsMap[mallId]) return;

      fetcher(`${apiHost}/malls/${mallId}`)
        .get()
        .notFound(() => {
          const newMallDetailsMap = {
            ...state.mallDetailsMap,
            [mallId]: null,
          };

          commit('setMallDetailsMap', newMallDetailsMap);
        })
        .json(data => {
          const newMallDetailsMap = {
            ...state.mallDetailsMap,
            [mallId]: data,
          };

          commit('setMallDetailsMap', newMallDetailsMap);
        });
    },
    async [actions.fetchMallDir]({ state, commit }, mallId) {
      if (state.mallDir[mallId]) return;

      try {
        const data = await fetcher(`${apiHost}/malls/${mallId}/directory`)
          .get()
          .json<IBusinessSummary[]>();

        let mallDir = data;

        // TODO: remove this
        if (!mallDir.length) {
          mallDir = fakeShopList;
        }

        const newMallDir = {
          ...state.mallDir,
          [mallId]: data,
        };

        commit('setMallDir', newMallDir);
      } catch (e) {
        // TODO: should properly log or show error notif
        console.log('error fetching mall dir: ', e);
      }
    },
    async [actions.fetchBizDetails]({ state, commit }, bizId) {
      if (state.bizDetailsMap[bizId]) return;

      fetcher(`${apiHost}/businesses/${bizId}`)
        .get()
        .notFound(() => {
          const newBizDetailsMap = {
            ...state.bizDetailsMap,
            [bizId]: null,
          };

          commit('setBizDetailsMap', newBizDetailsMap);
        })
        .json(data => {
          const newBizDetailsMap = {
            ...state.bizDetailsMap,
            [bizId]: data,
          };

          commit('setBizDetailsMap', newBizDetailsMap);
        })
        .catch(e => {
          // TODO: should properly log or show error notif
          console.log('error fetching biz details: ', e);
        });
    },
    async [actions.fetchBizCategories]({ state, commit }) {
      if (!isEmpty(state.bizCategories)) return;

      fetcher(`${apiHost}/business-categories`)
        .get()
        .json(data => {
          commit('setBizCategories', data);
        })
        .catch(e => {
          // TODO: should properly log or show error notif
          console.log('error fetching biz category: ', e);
        });
    },
  },
});
